import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { formatPrice } from "../lib/format";
import { track } from "../lib/analytics";
import { Tag, Check } from "lucide-react";

export default function Checkout() {
  const { items } = useCart();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [couponInput, setCouponInput] = useState("");
  const [couponCode, setCouponCode] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [loyaltyRedeem, setLoyaltyRedeem] = useState(0);

  useEffect(() => {
    if (items.length === 0) return;
    api
      .post("/cart/preview", {
        items: items.map((i) => ({
          product_id: i.product_id,
          quantity: i.quantity,
          variant_id: i.variant_id || null,
        })),
        coupon_code: couponCode,
        loyalty_redeem_eur: loyaltyRedeem,
      })
      .then((r) => setPreview(r.data))
      .catch(() => setPreview(null));
  }, [items, couponCode, loyaltyRedeem]);

  const subtotal = preview?.subtotal ?? items.reduce((s, i) => s + i.price * i.quantity, 0);
  const bundleDiscount = preview?.bundle_discount ?? 0;
  const bundleLabel = preview?.bundle_label || "";
  const couponDiscount = preview?.coupon_discount ?? 0;
  const appliedCoupon = preview?.coupon_code || "";
  const loyaltyDiscount = preview?.loyalty_discount ?? 0;
  const loyaltyPointsUsed = preview?.loyalty_points_used ?? 0;
  const loyaltyPointsAvailable = preview?.loyalty_points_available ?? 0;
  const loyaltyMaxRedeemable = preview?.loyalty_max_redeemable_eur ?? 0;
  const shipping = preview?.shipping ?? (subtotal >= 75 ? 0 : 4.90);
  const total = preview?.total ?? subtotal + shipping;

  const [form, setForm] = useState({
    full_name: user?.name || "",
    email: user?.email || "",
    address: "",
    city: "",
    postal_code: "",
    country: "France",
  });

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-24 text-center" data-testid="checkout-empty">
        <p className="text-sm text-white/60">{t("cart.empty")}.</p>
      </div>
    );
  }

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.address || !form.city || !form.postal_code) {
      toast.error(t("checkout.fill_all"));
      return;
    }
    setSubmitting(true);
    try {
      track("begin_checkout", {
        currency: "EUR",
        value: total,
        items: items.map((i) => ({ item_id: i.product_id, item_name: i.name, price: i.price, quantity: i.quantity })),
      });
      const r = await api.post("/checkout/session", {
        items: items.map((i) => ({
          product_id: i.product_id,
          quantity: i.quantity,
          variant_id: i.variant_id || null,
        })),
        origin_url: window.location.origin,
        shipping_address: form,
        coupon_code: couponCode || undefined,
        loyalty_redeem_eur: loyaltyRedeem || 0,
      });
      window.location.href = r.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || t("checkout.payment_error"));
      setSubmitting(false);
    }
  };

  const applyCoupon = async (e) => {
    e.preventDefault();
    const code = couponInput.trim().toUpperCase();
    if (!code) return;
    setCouponLoading(true);
    try {
      const r = await api.post("/coupons/validate", { code, subtotal });
      if (r.data?.discount > 0) {
        setCouponCode(code);
        toast.success(t("checkout.coupon_applied", { code, label: r.data.label || "" }));
        track("coupon_apply", { code });
      } else {
        toast.error(t("checkout.coupon_invalid"));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t("checkout.coupon_invalid_or_expired"));
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setCouponCode(null);
    setCouponInput("");
    toast.success(t("checkout.coupon_removed"));
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12" data-testid="checkout-page">
      <div className="border-b border-white/10 pb-8 mb-12">
        <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#C5CDD8]">{t("checkout.order")}</div>
        <h1 className="font-display mt-3 text-4xl sm:text-5xl font-bold uppercase tracking-tighter text-white">
          {t("checkout.payment")}
        </h1>
      </div>

      <div className="grid lg:grid-cols-2 gap-12">
        <form onSubmit={submit} className="space-y-6" data-testid="checkout-form">
          <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#C5CDD8]">{t("checkout.shipping_address")}</div>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label={t("checkout.full_name")} value={form.full_name} onChange={update("full_name")} testId="field-fullname" />
            <Field label={t("checkout.email")} type="email" value={form.email} onChange={update("email")} testId="field-email" />
          </div>
          <Field label={t("checkout.address")} value={form.address} onChange={update("address")} testId="field-address" />
          <div className="grid sm:grid-cols-3 gap-4">
            <Field label={t("checkout.city")} value={form.city} onChange={update("city")} testId="field-city" />
            <Field label={t("checkout.postal_code")} value={form.postal_code} onChange={update("postal_code")} testId="field-postal" />
            <Field label={t("checkout.country")} value={form.country} onChange={update("country")} testId="field-country" />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full border border-white bg-white text-[#001F3F] hover:bg-transparent hover:text-white transition-all uppercase tracking-[0.25em] text-xs font-bold py-5 disabled:opacity-60"
            data-testid="checkout-submit-btn"
          >
            {submitting ? t("checkout.redirecting") : `${t("checkout.pay")} ${formatPrice(total)}`}
          </button>
          <p className="text-xs text-white/60 text-center">
            {t("checkout.secure_payment_long")}
          </p>
        </form>

        <aside>
          <div className="border border-white/10 bg-[#0A2A4F] p-6 lg:p-8">
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#C5CDD8] mb-6">{t("checkout.your_order")}</div>
            <div className="space-y-4">
              {items.map((it) => (
                <div key={it.product_id + (it.variant_id || "")} className="flex gap-4 text-sm">
                  <div className="w-16 h-16 bg-[#001F3F] flex-shrink-0 border border-white/5">
                    <img src={it.image} alt={it.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-white truncate">{it.name}</div>
                    {it.variant_label && (
                      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#C5CDD8] mt-0.5">{it.variant_label}</div>
                    )}
                    <div className="text-xs text-white/60 mt-0.5">{t("checkout.qty")}: {it.quantity}</div>
                  </div>
                  <div className="font-bold text-white">{formatPrice(it.price * it.quantity)}</div>
                </div>
              ))}
            </div>
            <div className="mt-6 border-t border-white/10 pt-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-white/60">{t("common.subtotal")}</span>
                <span className="font-bold text-white">{formatPrice(subtotal)}</span>
              </div>
              {bundleDiscount > 0 && (
                <div className="flex justify-between" data-testid="checkout-bundle-line">
                  <span className="text-[#A6E0B5]">{bundleLabel}</span>
                  <span className="font-bold text-[#A6E0B5]">−{formatPrice(bundleDiscount)}</span>
                </div>
              )}
              {couponDiscount > 0 && (
                <div className="flex justify-between" data-testid="checkout-coupon-line">
                  <span className="text-[#A6E0B5] inline-flex items-center gap-1">
                    <Tag size={12} /> {t("checkout.code")} {appliedCoupon}
                  </span>
                  <span className="font-bold text-[#A6E0B5]">
                    −{formatPrice(couponDiscount)}
                  </span>
                </div>
              )}
              {loyaltyDiscount > 0 && (
                <div className="flex justify-between" data-testid="checkout-loyalty-line">
                  <span className="text-[#FFD700] inline-flex items-center gap-1">
                    ★ Points fidélité ({loyaltyPointsUsed} pts)
                  </span>
                  <span className="font-bold text-[#FFD700]">
                    −{formatPrice(loyaltyDiscount)}
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-white/60">{t("common.shipping")}</span>
                <span className="font-bold text-white">{shipping === 0 ? t("common.free") : formatPrice(shipping)}</span>
              </div>
              <div className="border-t border-white/10 pt-3 flex justify-between">
                <span className="font-bold uppercase tracking-widest text-xs text-white">{t("common.total")}</span>
                <span className="font-display text-xl font-bold text-white">{formatPrice(total)}</span>
              </div>
            </div>

            {/* Coupon form */}
            <div className="mt-6 pt-6 border-t border-white/10">
              {appliedCoupon ? (
                <div
                  className="flex items-center justify-between gap-3 p-3 border border-[#A6E0B5]/40 bg-[#A6E0B5]/5"
                  data-testid="coupon-applied"
                >
                  <span className="inline-flex items-center gap-2 text-xs text-[#A6E0B5]">
                    <Check size={14} /> {t("checkout.code")}{" "}
                    <strong className="font-bold">{appliedCoupon}</strong> {t("checkout.active")}
                  </span>
                  <button
                    type="button"
                    onClick={removeCoupon}
                    className="text-[10px] uppercase tracking-widest text-white/60 hover:text-white"
                    data-testid="coupon-remove"
                  >
                    {t("cart.remove")}
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={applyCoupon}
                  className="flex gap-2"
                  data-testid="coupon-form"
                >
                  <input
                    type="text"
                    placeholder={t("cart.coupon_placeholder")}
                    value={couponInput}
                    onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                    className="flex-1 bg-[#001F3F] border border-white/20 text-white px-3 py-2 text-sm focus:outline-none focus:border-white uppercase tracking-wider"
                    data-testid="coupon-input"
                  />
                  <button
                    type="submit"
                    disabled={couponLoading || !couponInput.trim()}
                    className="border border-white/40 px-4 py-2 text-[10px] font-bold uppercase tracking-widest text-white hover:bg-white hover:text-[#001F3F] transition disabled:opacity-40"
                    data-testid="coupon-submit"
                  >
                    {couponLoading ? "..." : t("cart.coupon_apply")}
                  </button>
                </form>
              )}
            </div>

            {/* Loyalty points redemption */}
            {user && loyaltyPointsAvailable > 0 && (
              <div className="mt-6 pt-6 border-t border-white/10" data-testid="loyalty-redeem-panel">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#FFD700]">
                    ★ Mes points fidélité
                  </div>
                  <div className="text-[10px] text-white/60">
                    {loyaltyPointsAvailable} pts disponibles
                  </div>
                </div>
                <p className="text-[11px] text-white/55 leading-relaxed">
                  Utilisez vos points (100 pts = 5 €). Maximum {formatPrice(loyaltyMaxRedeemable)} sur cette commande.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {[5, 10, 25, 50]
                    .filter((v) => v <= loyaltyMaxRedeemable)
                    .map((v) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => setLoyaltyRedeem((curr) => (curr === v ? 0 : v))}
                        className={`px-3 py-2 text-[11px] font-bold uppercase tracking-widest border transition-all ${
                          loyaltyRedeem === v
                            ? "bg-[#FFD700] text-[#001F3F] border-[#FFD700]"
                            : "bg-transparent text-white border-white/30 hover:border-white"
                        }`}
                        data-testid={`loyalty-redeem-${v}`}
                      >
                        −{v} €
                      </button>
                    ))}
                  {loyaltyMaxRedeemable > 0 && (
                    <button
                      type="button"
                      onClick={() => setLoyaltyRedeem(loyaltyMaxRedeemable)}
                      className={`px-3 py-2 text-[11px] font-bold uppercase tracking-widest border transition-all ${
                        loyaltyRedeem === loyaltyMaxRedeemable
                          ? "bg-[#FFD700] text-[#001F3F] border-[#FFD700]"
                          : "bg-transparent text-white border-white/30 hover:border-white"
                      }`}
                      data-testid="loyalty-redeem-max"
                    >
                      MAX (−{formatPrice(loyaltyMaxRedeemable)})
                    </button>
                  )}
                  {loyaltyRedeem > 0 && (
                    <button
                      type="button"
                      onClick={() => setLoyaltyRedeem(0)}
                      className="px-3 py-2 text-[11px] font-bold uppercase tracking-widest text-white/60 hover:text-white"
                      data-testid="loyalty-redeem-clear"
                    >
                      × Annuler
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testId }) {
  return (
    <label className="block">
      <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#C5CDD8]">{label}</span>
      <input
        type={type}
        required
        value={value}
        onChange={onChange}
        data-testid={testId}
        className="mt-2 w-full border-b border-white/40 bg-transparent text-white px-0 py-2 text-sm focus:outline-none focus:border-white"
      />
    </label>
  );
}
